import type { SVGProps } from 'react';

/**
 * Registre d'icônes SVG cohérent (style stroke, à la Lucide).
 * Toutes les icônes acceptent les mêmes props, héritent de `currentColor`
 * et s'alignent sur les ~24×24px par défaut (redimensionnables via size).
 *
 * Usage :
 *   <Icon name="home" size={20} />
 */
export type IconName =
    // Secteurs — bâtiment
    | 'construction'
    | 'renovation'
    | 'carpentry'
    | 'electricity'
    | 'plumbing'
    | 'hvac'
    | 'painting'
    | 'roofing'
    | 'landscaping'
    | 'pool'
    // Secteurs — services aux particuliers
    | 'cleaning'
    | 'moving'
    | 'home-care'
    | 'beauty'
    | 'automotive'
    // Secteurs — événementiel / créatif
    | 'catering'
    | 'photo'
    | 'music'
    | 'florist'
    // Secteurs — pro / B2B
    | 'consulting'
    | 'marketing'
    | 'training'
    | 'health'
    // Générique
    | 'briefcase'
    // Types de champs
    | 'list'
    | 'chips'
    | 'slider'
    | 'number'
    | 'radio'
    | 'textarea'
    | 'checkbox'
    // Divers UI
    | 'settings'
    | 'price'
    | 'branch'
    | 'plus'
    | 'x'
    | 'check'
    | 'arrow-right'
    | 'save'
    | 'loader'
    | 'globe'
    | 'palette'
    | 'users'
    | 'layers'
    | 'grid'
    | 'book'
    | 'code'
    | 'user'
    | 'logout'
    | 'mail'
    | 'chevron-right'
    | 'trending-up'
    | 'download'
    | 'shield'
    | 'alert-triangle'
    | 'key'
    | 'eye'
    | 'eye-off'
    | 'bell'
    | 'trash'
    | 'link'
    | 'copy'
    | 'image'
    | 'video'
    | 'camera'
    | 'info'
    | 'search'
    | 'home'
    | 'play'
    | 'file-text'
    | 'lightning'
    | 'compass'
    | 'sparkles';

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'ref'> {
    name: IconName;
    size?: number;
    strokeWidth?: number;
}

export function Icon({ name, size = 20, strokeWidth = 1.8, ...rest }: IconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
            {...rest}
        >
            {PATHS[name]}
        </svg>
    );
}

/* ================================================================
   Paths — chaque icône est un <g> / <path> inline.
   Style : stroke, 24x24, line caps ronds.
   ================================================================ */
const PATHS: Record<IconName, JSX.Element> = {
    // ========== BÂTIMENT ==========
    construction: (
        <g>
            <rect x="3" y="13" width="18" height="8" rx="1" />
            <path d="M5 13V8l7-4 7 4v5" />
            <path d="M9 21v-4h6v4" />
        </g>
    ),
    renovation: (
        <g>
            <path d="M3 21h18" />
            <path d="M5 21V10l7-5 7 5v11" />
            <path d="M10 21v-6h4v6" />
            <path d="M15 7l4 4" />
        </g>
    ),
    carpentry: (
        <g>
            <path d="M4 21l16-16" />
            <path d="M14 5l5 5" />
            <path d="M3 21h7" />
            <path d="M8 14l2 2" />
        </g>
    ),
    electricity: (
        <g>
            <polygon points="13,2 4,14 12,14 11,22 20,10 12,10" />
        </g>
    ),
    plumbing: (
        <g>
            <path d="M3 9h6v6" />
            <path d="M9 9h6v6h6" />
            <path d="M3 9V5" />
            <path d="M15 15v4" />
        </g>
    ),
    hvac: (
        <g>
            <rect x="2" y="5" width="20" height="10" rx="2" />
            <path d="M6 9h12" />
            <path d="M6 12h12" />
            <path d="M7 19l2-3" />
            <path d="M12 19l2-3" />
            <path d="M17 19l2-3" />
        </g>
    ),
    painting: (
        <g>
            <path d="M3 3h14v4H3z" />
            <path d="M17 5h4v6h-4" />
            <path d="M10 7v5H7v5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2v-5h-3" />
        </g>
    ),
    roofing: (
        <g>
            <path d="M2 12L12 3l10 9" />
            <path d="M5 11v9h14v-9" />
            <path d="M10 20v-5h4v5" />
        </g>
    ),
    landscaping: (
        <g>
            <path d="M7 20c0-5 2-8 5-8s5 3 5 8" />
            <path d="M12 12V4" />
            <path d="M12 8c-2-2-4-2-5 0" />
            <path d="M12 8c2-2 4-2 5 0" />
            <path d="M3 20h18" />
        </g>
    ),
    pool: (
        <g>
            <path d="M2 18c2 0 2 2 4 2s2-2 4-2 2 2 4 2 2-2 4-2 2 2 4 2" />
            <path d="M2 14c2 0 2 2 4 2s2-2 4-2 2 2 4 2 2-2 4-2 2 2 4 2" />
            <path d="M7 10V5a2 2 0 0 1 2-2h0" />
            <path d="M17 10V5a2 2 0 0 0-2-2h0" />
        </g>
    ),

    // ========== SERVICES AUX PARTICULIERS ==========
    cleaning: (
        <g>
            <path d="M8 2h8l-1 6H9z" />
            <path d="M10 8v3" />
            <path d="M14 8v3" />
            <path d="M7 11h10l-1 10H8z" />
        </g>
    ),
    moving: (
        <g>
            <rect x="3" y="7" width="13" height="10" rx="1" />
            <path d="M16 10h4l2 4v3h-6z" />
            <circle cx="7.5" cy="19" r="1.8" />
            <circle cx="17.5" cy="19" r="1.8" />
        </g>
    ),
    'home-care': (
        <g>
            <path d="M3 11l9-7 9 7" />
            <path d="M5 10v10h14V10" />
            <path d="M10 17a2 2 0 1 1 4 0" />
            <circle cx="12" cy="13" r="1.3" />
        </g>
    ),
    beauty: (
        <g>
            <path d="M12 2c-3 3-4 6-4 9a4 4 0 0 0 8 0c0-3-1-6-4-9z" />
            <path d="M7 15c-2 1-3 3-3 5" />
            <path d="M17 15c2 1 3 3 3 5" />
        </g>
    ),
    automotive: (
        <g>
            <path d="M5 17h14l-1.5-6a2 2 0 0 0-2-1.5h-7a2 2 0 0 0-2 1.5z" />
            <circle cx="8" cy="18" r="1.8" />
            <circle cx="16" cy="18" r="1.8" />
            <path d="M5 13h14" />
        </g>
    ),

    // ========== ÉVÉNEMENTIEL / CRÉATIF ==========
    catering: (
        <g>
            <path d="M3 12h18" />
            <path d="M3 12c0-5 4-8 9-8s9 3 9 8" />
            <path d="M2 17h20" />
            <path d="M5 20h14" />
        </g>
    ),
    photo: (
        <g>
            <path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
            <circle cx="12" cy="13" r="3.5" />
        </g>
    ),
    music: (
        <g>
            <path d="M9 18V5l11-2v13" />
            <circle cx="6" cy="18" r="3" />
            <circle cx="17" cy="16" r="3" />
        </g>
    ),
    florist: (
        <g>
            <circle cx="12" cy="8" r="2.5" />
            <circle cx="7" cy="10" r="2.5" />
            <circle cx="17" cy="10" r="2.5" />
            <circle cx="9.5" cy="5" r="2.5" />
            <circle cx="14.5" cy="5" r="2.5" />
            <path d="M12 11v10" />
            <path d="M12 17c-1-1-3-1-3-3" />
            <path d="M12 17c1-1 3-1 3-3" />
        </g>
    ),

    // ========== PRO / B2B ==========
    consulting: (
        <g>
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21v-2a5 5 0 0 1 5-5h6a5 5 0 0 1 5 5v2" />
        </g>
    ),
    marketing: (
        <g>
            <path d="M3 11v2" />
            <path d="M20 4v16l-13-3V7z" />
            <path d="M9 17v4h3l-1-4" />
        </g>
    ),
    training: (
        <g>
            <path d="M12 3L2 8l10 5 10-5-10-5z" />
            <path d="M6 10v5c0 2 3 4 6 4s6-2 6-4v-5" />
            <path d="M22 8v6" />
        </g>
    ),
    health: (
        <g>
            <path d="M4 12h4l2-4 4 8 2-4h4" />
        </g>
    ),

    briefcase: (
        <g>
            <rect x="3" y="7" width="18" height="13" rx="2" />
            <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            <path d="M3 13h18" />
        </g>
    ),

    // ========== CHAMPS ==========
    list: (
        <g>
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M7 9h10" />
            <path d="M7 13h10" />
            <path d="M7 17h6" />
        </g>
    ),
    chips: (
        <g>
            <rect x="2" y="7" width="9" height="4" rx="2" />
            <rect x="13" y="7" width="9" height="4" rx="2" />
            <rect x="2" y="13" width="9" height="4" rx="2" />
            <rect x="13" y="13" width="9" height="4" rx="2" />
        </g>
    ),
    slider: (
        <g>
            <path d="M4 12h16" />
            <circle cx="9" cy="12" r="3" fill="currentColor" />
        </g>
    ),
    number: (
        <g>
            <path d="M4 9h16" />
            <path d="M4 15h16" />
            <path d="M10 3L8 21" />
            <path d="M16 3l-2 18" />
        </g>
    ),
    radio: (
        <g>
            <circle cx="12" cy="12" r="8" />
            <circle cx="12" cy="12" r="3" fill="currentColor" />
        </g>
    ),
    textarea: (
        <g>
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M7 9h10" />
            <path d="M7 13h10" />
            <path d="M7 17h5" />
        </g>
    ),
    checkbox: (
        <g>
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M7 12l3 3 7-7" />
        </g>
    ),

    // ========== UI ==========
    settings: (
        <g>
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </g>
    ),
    price: (
        <g>
            <path d="M12 1v22" />
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </g>
    ),
    branch: (
        <g>
            <circle cx="6" cy="5" r="2.5" />
            <circle cx="18" cy="19" r="2.5" />
            <circle cx="18" cy="5" r="2.5" />
            <path d="M6 7.5v2A6 6 0 0 0 12 15.5h3" />
            <path d="M18 7.5v9" />
        </g>
    ),
    plus: (
        <g>
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
        </g>
    ),
    x: (
        <g>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </g>
    ),
    check: (
        <polyline points="20 6 9 17 4 12" />
    ),
    'arrow-right': (
        <polyline points="9 18 15 12 9 6" />
    ),
    save: (
        <g>
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
            <polyline points="17 21 17 13 7 13 7 21" />
            <polyline points="7 3 7 8 15 8" />
        </g>
    ),
    loader: (
        <g>
            <line x1="12" y1="2" x2="12" y2="6" />
            <line x1="12" y1="18" x2="12" y2="22" />
            <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
            <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
            <line x1="2" y1="12" x2="6" y2="12" />
            <line x1="18" y1="12" x2="22" y2="12" />
            <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
            <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
        </g>
    ),
    globe: (
        <g>
            <circle cx="12" cy="12" r="9" />
            <path d="M3 12h18" />
            <path d="M12 3a14 14 0 0 1 0 18" />
            <path d="M12 3a14 14 0 0 0 0 18" />
        </g>
    ),
    palette: (
        <g>
            <circle cx="13.5" cy="6.5" r="1.5" fill="currentColor" />
            <circle cx="17.5" cy="10.5" r="1.5" fill="currentColor" />
            <circle cx="8.5" cy="7.5" r="1.5" fill="currentColor" />
            <circle cx="6.5" cy="12.5" r="1.5" fill="currentColor" />
            <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c1 0 2-1 2-2 0-.5-.2-1-.5-1.3-.3-.4-.5-.8-.5-1.3 0-1 .9-2 2-2h2.2c2.6 0 4.8-2.1 4.8-4.7C22 5.9 17.5 2 12 2z" />
        </g>
    ),
    users: (
        <g>
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </g>
    ),
    layers: (
        <g>
            <polygon points="12 2 2 7 12 12 22 7 12 2" />
            <polyline points="2 17 12 22 22 17" />
            <polyline points="2 12 12 17 22 12" />
        </g>
    ),
    grid: (
        <g>
            <rect x="3" y="3" width="7" height="7" rx="1.5" />
            <rect x="14" y="3" width="7" height="7" rx="1.5" />
            <rect x="3" y="14" width="7" height="7" rx="1.5" />
            <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </g>
    ),
    book: (
        <g>
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5V4.5A2.5 2.5 0 0 1 6.5 2z" />
        </g>
    ),
    code: (
        <g>
            <polyline points="16 18 22 12 16 6" />
            <polyline points="8 6 2 12 8 18" />
        </g>
    ),
    user: (
        <g>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
        </g>
    ),
    logout: (
        <g>
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
        </g>
    ),
    mail: (
        <g>
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <polyline points="3 7 12 13 21 7" />
        </g>
    ),
    'chevron-right': (
        <polyline points="9 18 15 12 9 6" />
    ),
    'trending-up': (
        <g>
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
            <polyline points="17 6 23 6 23 12" />
        </g>
    ),
    download: (
        <g>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
        </g>
    ),
    shield: (
        <g>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </g>
    ),
    'alert-triangle': (
        <g>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
        </g>
    ),
    key: (
        <g>
            <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
        </g>
    ),
    eye: (
        <g>
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
        </g>
    ),
    'eye-off': (
        <g>
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
            <line x1="1" y1="1" x2="23" y2="23" />
        </g>
    ),
    bell: (
        <g>
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </g>
    ),
    trash: (
        <g>
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
            <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
        </g>
    ),
    link: (
        <g>
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </g>
    ),
    copy: (
        <g>
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </g>
    ),
    image: (
        <g>
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
        </g>
    ),
    video: (
        <g>
            <polygon points="23 7 16 12 23 17 23 7" />
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
        </g>
    ),
    camera: (
        <g>
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
        </g>
    ),
    info: (
        <g>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
        </g>
    ),
    search: (
        <g>
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </g>
    ),
    home: (
        <g>
            <path d="M3 9.5L12 3l9 6.5V21a1 1 0 0 1-1 1h-5v-7h-6v7H4a1 1 0 0 1-1-1z" />
        </g>
    ),
    play: (
        <g>
            <polygon points="5 3 19 12 5 21 5 3" />
        </g>
    ),
    'file-text': (
        <g>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <line x1="10" y1="9" x2="8" y2="9" />
        </g>
    ),
    lightning: (
        <g>
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </g>
    ),
    compass: (
        <g>
            <circle cx="12" cy="12" r="10" />
            <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
        </g>
    ),
    sparkles: (
        <g>
            <path d="M12 3l2 5 5 2-5 2-2 5-2-5-5-2 5-2z" />
            <path d="M19 14l1 2 2 1-2 1-1 2-1-2-2-1 2-1z" />
        </g>
    ),
};
